import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, Trash2, Heart, Pin, Lock, Unlock, Eye, Quote, X, Pencil, Ban } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { usePublicConfig } from '../lib/publicConfig';
import RichContentBlocksEditor from '../components/RichContentBlocksEditor';
import RichTextEditor from '../components/RichTextEditor';
import InternalAccessGuard from '../components/InternalAccessGuard';
import DOMPurify from 'dompurify';
import { PostBubble } from '../components/forum/PostBubble';
import { quoteHtmlFromPost } from '../components/forum/forumUtils';
import useForumAccess from '../components/forum/useForumAccess';

export default function ForumThreadPage() {
  return (
    <InternalAccessGuard>
      <ThreadInner />
    </InternalAccessGuard>
  );
}

function ThreadInner() {
  const { lang, id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const publicCfg = usePublicConfig();
  const access = useForumAccess();
  const mp = access.mod_perms || [];
  const canModerate = mp.includes('moderate_thread');
  const canEditOthers = mp.includes('edit_post');
  const canDeleteOthers = mp.includes('delete_post');
  const canDeleteThreads = mp.includes('delete_thread');
  const canBlockUsers = mp.includes('block_user');
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [replyBlocks, setReplyBlocks] = useState([]);
  const [replyToPost, setReplyToPost] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);   // { post, isThread }
  const [editHtml, setEditHtml] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [blocking, setBlocking] = useState(null);  // { post }
  const [blockUnit, setBlockUnit] = useState('permanent');
  const [blockValue, setBlockValue] = useState(7);
  const [blockingBusy, setBlockingBusy] = useState(false);
  const base = `/${lang}/${lang === 'en' ? 'forum' : 'foro'}`;
  const hubBase = `/${lang}/${lang === 'en' ? 'internal' : 'interno'}`;

  async function load() {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/forum/threads/${id}`);
      setThread(data.thread);
      setPosts(data.posts || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Forum master switch: when OFF, only aviso-discussion threads (from_aviso_id)
  // remain accessible — general forum threads are blocked for EVERYONE.
  useEffect(() => {
    if (!thread || !publicCfg) return;
    const forumOff = publicCfg.forum_enabled === false;
    if (forumOff && !thread.from_aviso_id) {
      navigate('/mi-perfil', { replace: true });
    }
  }, [thread, publicCfg, navigate]);

  async function toggleLike() {
    if (!thread) return;
    try {
      const { data } = await api.post(`/forum/threads/${id}/like`);
      setThread((t) => ({ ...t, liked_by_me: data.liked, liked_count: data.liked_count }));
    } catch (e) { /* silent */ }
  }

  async function moderate(patch) {
    try {
      await api.post(`/forum/threads/${id}/moderate`, patch);
      setThread((t) => ({ ...t, ...patch }));
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  }

  function scrollToReply() {
    const el = document.getElementById('forum-reply-box');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('.ql-editor');
      if (input) setTimeout(() => input.focus(), 400);
    }
  }

  function startReply(post) {
    setReplyToPost({
      post_id: post.id,
      author_name: post.author_name,
      body_html: quoteHtmlFromPost(post),
    });
    scrollToReply();
  }

  async function sendReply() {
    const htmlEmpty = !replyHtml || !replyHtml.replace(/<(.|\n)*?>/g, '').trim();
    if (htmlEmpty && !reply.trim() && replyBlocks.length === 0) return;
    setSending(true); setErr('');
    try {
      const payload = { body: reply, body_html: htmlEmpty ? '' : replyHtml, body_blocks: replyBlocks };
      if (replyToPost) {
        payload.quote = {
          author_name: replyToPost.author_name,
          body_html: replyToPost.body_html || '',
          post_id: replyToPost.post_id,
        };
      }
      await api.post(`/forum/threads/${id}/posts`, payload);
      setReply('');
      setReplyHtml('');
      setReplyBlocks([]);
      setReplyToPost(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSending(false); }
  }

  async function deletePost(postId) {
    if (!window.confirm('¿Eliminar esta respuesta?')) return;
    try {
      await api.delete(`/forum/posts/${postId}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  }

  async function deleteThread() {
    if (!window.confirm('¿Eliminar todo el hilo? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/forum/threads/${id}`);
      window.location.href = thread?.from_aviso_id
        ? `${hubBase}?tab=avisos`
        : `${base}/c/${thread?.category_slug}`;
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  }

  function startEdit(post) {
    const isThread = post.id === 'op';
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    setEditing({ post, isThread });
    setEditHtml(post.body_html || (post.body ? `<p>${esc(post.body)}</p>` : ''));
    setEditTitle(isThread ? (thread?.title || '') : '');
  }

  async function saveEdit() {
    if (!editing) return;
    const htmlEmpty = !editHtml || !editHtml.replace(/<(.|\n)*?>/g, '').trim();
    if (htmlEmpty) { setErr('El mensaje no puede quedar vacío.'); return; }
    setSavingEdit(true); setErr('');
    try {
      if (editing.isThread) {
        const payload = { body_html: editHtml, body: '' };
        if (editTitle.trim()) payload.title = editTitle.trim();
        await api.patch(`/forum/threads/${id}`, payload);
      } else {
        await api.patch(`/forum/posts/${editing.post.id}`, { body_html: editHtml, body: '' });
      }
      setEditing(null); setEditHtml(''); setEditTitle('');
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error al editar');
    } finally { setSavingEdit(false); }
  }

  function openBlock(post) {
    setBlocking({ post });
    setBlockUnit('permanent');
    setBlockValue(7);
  }

  async function submitBlock() {
    if (!blocking) return;
    const authorId = blocking.post.author_id;
    if (!authorId) { setErr('No se pudo identificar al autor.'); setBlocking(null); return; }
    setBlockingBusy(true); setErr('');
    try {
      const payload = { user_id: authorId, unit: blockUnit };
      if (blockUnit !== 'permanent') payload.value = Number(blockValue);
      await api.post('/forum/moderate/block-user', payload);
      setBlocking(null);
      alert('Usuario bloqueado del foro.');
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error al bloquear');
    } finally { setBlockingBusy(false); }
  }

  const canDeleteThread = user && thread && (canDeleteThreads || thread.author_id === user.id);
  const locked = thread?.is_locked;

  return (
    <div data-testid="forum-thread-page" className="bg-gray-50 min-h-[60vh] pt-20">
      <section className="bg-white border-b border-gray-200 py-5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={
              thread
                ? (thread.from_aviso_id ? `${hubBase}?tab=avisos` : `${base}/c/${thread.category_slug}`)
                : `${base}?tab=foro`
            }
            className="inline-flex items-center gap-1.5 text-xs text-cmic-600 hover:text-cmic-700 mb-2"
          >
            <ArrowLeft size={14} /> {thread ? (thread.from_aviso_id ? 'Avisos' : thread.category_name) : 'Foro'}
          </Link>
          {thread && (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {thread.is_pinned && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded"><Pin size={9} /> Fijado</span>}
                  {locked && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-charcoal-200 text-charcoal-600 px-2 py-0.5 rounded"><Lock size={9} /> Cerrado</span>}
                  {typeof thread.view_count === 'number' && <span className="inline-flex items-center gap-1 text-[11px] text-charcoal-400" data-testid="forum-thread-views"><Eye size={11} /> {thread.view_count} {thread.view_count === 1 ? 'vista' : 'vistas'}</span>}
                  <span className="inline-flex items-center gap-1 text-[11px] text-charcoal-400"><Send size={10} /> {thread.reply_count ?? 0} {(thread.reply_count ?? 0) === 1 ? 'respuesta' : 'respuestas'}</span>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-charcoal-800 break-words">{thread.title}</h1>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={toggleLike}
                  data-testid="forum-thread-like"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${thread.liked_by_me ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  <Heart size={14} className={thread.liked_by_me ? 'fill-rose-500 text-rose-500' : ''} /> {thread.liked_count ?? (thread.liked_by || []).length ?? 0}
                </button>
                {canModerate && (
                  <>
                    <button onClick={() => moderate({ is_pinned: !thread.is_pinned })} title={thread.is_pinned ? 'Desfijar' : 'Fijar'} data-testid="forum-thread-pin"
                      className={`p-2 rounded-lg border border-gray-200 ${thread.is_pinned ? 'text-amber-600 bg-amber-50' : 'text-charcoal-500 hover:bg-gray-50'}`}>
                      <Pin size={15} />
                    </button>
                    <button onClick={() => moderate({ is_locked: !thread.is_locked })} title={locked ? 'Reabrir' : 'Cerrar'} data-testid="forum-thread-lock"
                      className={`p-2 rounded-lg border border-gray-200 ${locked ? 'text-charcoal-700 bg-charcoal-100' : 'text-charcoal-500 hover:bg-gray-50'}`}>
                      {locked ? <Unlock size={15} /> : <Lock size={15} />}
                    </button>
                  </>
                )}
                {canDeleteThread && (
                  <button
                    onClick={deleteThread}
                    title="Eliminar hilo"
                    className="p-2 rounded-lg text-charcoal-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-3">
        {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{err}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
        ) : !thread ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">Hilo no encontrado.</div>
        ) : (
          <>
            <PostBubble
              post={{
                id: 'op',
                author_id: thread.author_id,
                author_name: thread.author_name,
                author_role: thread.author_role,
                author_title: thread.author_title,
                author_photo_url: thread.author_photo_url,
                author_joined_at: thread.author_joined_at,
                author_message_count: thread.author_message_count,
                body: thread.body,
                body_html: thread.body_html,
                body_blocks: thread.body_blocks,
                created_at: thread.created_at,
                edited_at: thread.edited_at,
                edited_by: thread.edited_by,
                edited_by_mod: thread.edited_by_mod,
              }}
              isFirst
              index={1}
              canDelete={false}
              canEdit={user && (canEditOthers || thread.author_id === user.id)}
              onEdit={startEdit}
              canBlock={canBlockUsers && user && thread.author_id !== user.id}
              onBlock={openBlock}
              onReply={(!locked || canModerate) ? startReply : undefined}
            />
            {posts.map((p, i) => (
              <PostBubble
                key={p.id}
                post={p}
                isFirst={false}
                index={i + 2}
                canDelete={user && (canDeleteOthers || p.author_id === user.id)}
                onDelete={deletePost}
                canEdit={user && (canEditOthers || p.author_id === user.id)}
                onEdit={startEdit}
                canBlock={canBlockUsers && user && p.author_id !== user.id}
                onBlock={openBlock}
                onReply={(!locked || canModerate) ? startReply : undefined}
              />
            ))}
            {!access.can_reply ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4 text-center text-sm text-amber-700 flex items-center justify-center gap-2" data-testid="forum-noreply-notice">
                <Lock size={15} /> {access.level === 'read' ? 'Tu acceso al foro es de solo lectura: puedes leer pero no responder.' : 'No tienes permiso para responder en el foro.'}
              </div>
            ) : (locked && !canModerate) ? (
              <div className="bg-charcoal-50 border border-charcoal-200 rounded-xl p-4 mt-4 text-center text-sm text-charcoal-500 flex items-center justify-center gap-2" data-testid="forum-thread-locked-notice">
                <Lock size={15} /> Este hilo está cerrado a nuevas respuestas.
              </div>
            ) : (
            <div id="forum-reply-box" className="bg-white border border-slate-200 rounded-3xl p-6 mt-6 shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Responder {locked && canModerate && <span className="text-amber-600">(hilo cerrado — respondes como moderador)</span>}
              </label>
              {replyToPost && (
                <div data-testid="forum-reply-quote-indicator" className="mb-3 flex items-start justify-between gap-2 rounded-xl border border-cmic-200 bg-cmic-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-cmic-700 flex items-center gap-1">
                      <Quote size={12} className="rotate-180" /> Respondiendo a {replyToPost.author_name}
                    </div>
                    <div
                      className="text-xs text-slate-500 mt-0.5 line-clamp-2 forum-rich-content"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(replyToPost.body_html || '') }}
                    />
                  </div>
                  <button
                    onClick={() => setReplyToPost(null)}
                    data-testid="forum-reply-quote-cancel"
                    title="Cancelar cita"
                    className="flex-shrink-0 p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <RichTextEditor
                value={replyHtml}
                onChange={setReplyHtml}
                testid="forum-reply-input"
                placeholder="Escribe tu respuesta…"
                minHeight={130}
              />
              <div className="mt-3">
                <RichContentBlocksEditor
                  value={replyBlocks}
                  onChange={setReplyBlocks}
                />
              </div>
              <div className="flex justify-end mt-2">
                <button
                  onClick={sendReply}
                  disabled={sending || (!replyHtml.replace(/<(.|\n)*?>/g, '').trim() && !reply.trim() && replyBlocks.length === 0)}
                  data-testid="forum-reply-send"
                  className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-600 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Publicar respuesta
                </button>
              </div>
            </div>
            )}
          </>
        )}
      </section>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="forum-edit-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h2 className="font-bold text-charcoal-800 text-base flex items-center gap-2">
                <Pencil size={16} className="text-cmic-600" /> Editar {editing.isThread ? 'hilo' : 'publicación'}
              </h2>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {editing.isThread && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                  <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="forum-edit-title" maxLength={160}
                    className="w-full rounded-lg border border-charcoal-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/20" />
                </div>
              )}
              <label className="block text-sm font-medium text-slate-700 mb-1">Contenido</label>
              <RichTextEditor value={editHtml} onChange={setEditHtml} testid="forum-edit-input" minHeight={160} />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
              <button onClick={saveEdit} disabled={savingEdit} data-testid="forum-edit-save"
                className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-600 disabled:opacity-60">
                {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {blocking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="forum-block-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h2 className="font-bold text-charcoal-800 text-base flex items-center gap-2">
                <Ban size={16} className="text-orange-600" /> Bloquear del foro
              </h2>
              <button onClick={() => setBlocking(null)} className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-charcoal-600">
                Vas a bloquear a <strong>{blocking.post.author_name}</strong> del foro. No podrá ver ni publicar mientras dure el bloqueo.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duración</label>
                <select value={blockUnit} onChange={(e) => setBlockUnit(e.target.value)} data-testid="forum-block-unit"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <option value="permanent">Permanente</option>
                  <option value="days">Días</option>
                  <option value="months">Meses</option>
                  <option value="years">Años</option>
                </select>
              </div>
              {blockUnit !== 'permanent' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Cantidad de {blockUnit === 'days' ? 'días' : blockUnit === 'months' ? 'meses' : 'años'}
                  </label>
                  <input type="number" min={1} value={blockValue} onChange={(e) => setBlockValue(e.target.value)} data-testid="forum-block-value"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  <p className="text-[11px] text-charcoal-400 mt-1">Se desbloqueará automáticamente al cumplirse el plazo.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setBlocking(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
              <button onClick={submitBlock} disabled={blockingBusy} data-testid="forum-block-confirm"
                className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 disabled:opacity-60">
                {blockingBusy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Bloquear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
